import React, { useContext, useMemo, useState } from 'react';

import CircularProgress from '@material-ui/core/CircularProgress';
import cx from 'classnames';
import { TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL } from 'config/urls';

import StudentProgressBox from 'pages/Teachers/ClassroomInsights/components/StudentProgressBox';
import StudentWorkDialog from 'pages/Teachers/ClassroomInsights/components/StudentWorkDialog';
import { shouldDisablePublish } from 'pages/Teachers/ClassroomInsights/utils';
import { trackMixpanelEvent } from 'utils/integrations/mixpanel';

import StudentSearch from './components/StudentsSearch';
import UserContext from 'components/UserContext';

import { MASTERY_LEVEL_TO_LABEL_MAPPER } from '../../constants';
import styles from './style.module.scss';

const INSIGHTS_CHAPTER_TWO_ENABLED =
  process.env.REACT_APP_INSIGHTS_CHAPTER_TWO_ENABLED === 'true';

const StudentProgress = ({
  students,
  allStudents,
  isLoadingData,
  onActionSuccess,
  sectionId,
  objectiveValueType,
  filters,
  setFilters
}) => {
  const { user } = useContext(UserContext);
  const [activeStudentIndex, setActiveStudentIndex] = useState(null);
  const viewableStudents = useMemo(
    () =>
      students?.filter((student) => student.student_tag_assignment_id !== null),
    [students]
  );

  const studentOptions = useMemo(
    () =>
      allStudents?.map((student) => ({
        student_id: student.student_id,
        name: student.student_name
      })),
    [allStudents]
  );

  const handleBoxClick = ({ student }) => {
    const index = viewableStudents.findIndex(
      (s) => s.student_tag_assignment_id === student.student_tag_assignment_id
    );
    if (index !== -1) setActiveStudentIndex(index);

    trackMixpanelEvent(user.id, 'Student tile clicked', {
      feature: 'live classroom insights',
      view: 'student progress',
      button: 'student tile',
      class_id: sectionId,
      normalizedURL:
        process.env.REACT_APP_BASE_FRONTEND_URL +
        TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
    });
  };

  const currentStudent =
    activeStudentIndex !== null ? viewableStudents[activeStudentIndex] : null;

  return (
    <>
      <header className={styles.progressHeader}>
        <div>
          <h3 className={styles.heading}>Students Progress</h3>
        </div>
        {INSIGHTS_CHAPTER_TWO_ENABLED && (
          <StudentSearch
            students={studentOptions}
            nameValue={filters.studentIds}
            onStudentSelect={(studentIds) =>
              setFilters({ ...filters, studentIds: studentIds })
            }
          />
        )}
      </header>
      <div
        className={cx(styles.progressDashboard, {
          [styles.hasFilters]: filters.studentIds.length > 0
        })}
      >
        {isLoadingData && <CircularProgress />}
        {students?.map((student, index) => {
          const isPublished = shouldDisablePublish({
            status: student.tracker_score_student_status,
            overallResponseStatus: student.tag_description
          });
          return (
            <StudentProgressBox
              key={`studentprogress-${index}`}
              name={student.student_name}
              status={MASTERY_LEVEL_TO_LABEL_MAPPER[student.tag_description]}
              masteryLabel={student.tag_description}
              grade={student.student_work_score?.toLowerCase()}
              isPublished={isPublished}
              onClick={() => handleBoxClick({ student })}
            />
          );
        })}

        {activeStudentIndex !== null && (
          <StudentWorkDialog
            currentStudent={currentStudent}
            objectiveValueType={objectiveValueType}
            status={
              MASTERY_LEVEL_TO_LABEL_MAPPER[currentStudent.tag_description]
            }
            onClose={() => {
              setActiveStudentIndex(null);
            }}
            onNext={() =>
              setActiveStudentIndex((prev) =>
                Math.min(prev + 1, viewableStudents.length - 1)
              )
            }
            onPrev={() =>
              setActiveStudentIndex((prev) => Math.max(prev - 1, 0))
            }
            onActionSuccess={onActionSuccess}
            sectionId={sectionId}
          />
        )}
      </div>
    </>
  );
};

export default StudentProgress;
